import { GetMeResponse, User } from "@/types/auth.type";
import { API_STATUS } from "@/constants";

/**
 * Mock user data for testing and development
 */
export const mockUser: User = {
  account: {
    user_id: "usr_12345678-1234-1234-1234-123456789abc",
    username: "admin@example.com",
  },
  apis: [
    "/api/platform/auth/v1/me",
    "/api/platform/organization-admin/v1/accounts",
    "/api/platform/organization-admin/v1/groups",
    "/api/platform/organization-admin/v1/roles",
    "/api/platform/organization-admin/v1/permissions",
  ],
  screens: ["account-management", "group-management", "role-management", "dashboard", "settings"],
  session: {
    expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours from now
    metadata: {
      access_token_id: "token_87654321-4321-4321-4321-cba987654321",
    },
    user_id: "usr_12345678-1234-1234-1234-123456789abc",
  },
  user_profile: {
    created_time: "2023-01-15T10:30:00.000Z",
    custom_attributes: {
      data_processing_consent: true,
      marketing_consent: false,
      terms_accepted: true,
      terms_accepted_at: "2023-01-15T10:35:00.000Z",
    },
    display_name: "John Admin",
    email: "admin@example.com",
    first_name: "John",
    id: "profile_12345678-1234-1234-1234-123456789abc",
    last_name: "Admin",
    last_updated_time: "2024-12-18T08:15:30.000Z",
    locale: "en-US",
    phone_number: "+1-555-0123",
    timezone: "America/New_York",
    user_id: "usr_12345678-1234-1234-1234-123456789abc",
    home_address: "123 Admin Street, Tech City, TC 12345",
    gender: "male",
    date_of_birth: "1985-06-15",
    birthday: "1985-06-15",
    picture_url:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
  },
};

/**
 * Mock response for getCurrentUser API
 */
export const mockGetCurrentUserResponse: GetMeResponse = {
  success: true,
  data: [mockUser],
  total: 1,
  timestamp: new Date().toISOString(),
  message: "User data retrieved successfully",
  status: API_STATUS.OK,
};

/**
 * Alternative mock users for different scenarios
 */
export const mockUsers = {
  admin: mockUser,

  regularUser: {
    ...mockUser,
    account: {
      user_id: "usr_87654321-4321-4321-4321-cba987654321",
      username: "user@example.com",
    },
    apis: ["/api/platform/auth/v1/me", "/api/platform/organization-admin/v1/accounts/read"],
    screens: ["dashboard"],
    session: {
      ...mockUser.session,
      user_id: "usr_87654321-4321-4321-4321-cba987654321",
      metadata: {
        access_token_id: "token_11111111-2222-3333-4444-555555555555",
      },
    },
    user_profile: {
      ...mockUser.user_profile,
      id: "profile_87654321-4321-4321-4321-cba987654321",
      user_id: "usr_87654321-4321-4321-4321-cba987654321",
      display_name: "Jane User",
      email: "user@example.com",
      first_name: "Jane",
      last_name: "User",
      gender: "female",
      picture_url:
        "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
    },
  } as User,

  expiredSession: {
    ...mockUser,
    session: {
      ...mockUser.session,
      expires_at: new Date(Date.now() - 60 * 60 * 1000).toISOString(), // 1 hour ago (expired)
    },
  } as User,
};

/**
 * Mock function that simulates the getCurrentUser API call
 * @param scenario - Which user scenario to return
 * @param delay - Optional delay in milliseconds to simulate network latency
 */
export async function mockGetCurrentUser(
  scenario: "admin" | "regularUser" | "expiredSession" = "admin",
  delay: number = 500
): Promise<GetMeResponse> {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, delay));

  const userData = mockUsers[scenario];

  return {
    success: true,
    data: [userData],
    total: 1,
    timestamp: new Date().toISOString(),
    message: "User data retrieved successfully",
    status: API_STATUS.OK,
  };
}

/**
 * Mock function that simulates an error response
 */
export async function mockGetCurrentUserError(
  errorType: "unauthorized" | "forbidden" | "server_error" = "unauthorized",
  delay: number = 500
): Promise<GetMeResponse> {
  await new Promise(resolve => setTimeout(resolve, delay));

  const errorResponses = {
    unauthorized: {
      success: false,
      data: [],
      timestamp: new Date().toISOString(),
      message: "Unauthorized access",
      error_code: "UNAUTHORIZED",
      status: API_STATUS.UNAUTHORIZED,
    },
    forbidden: {
      success: false,
      data: [],
      timestamp: new Date().toISOString(),
      message: "Access forbidden",
      error_code: "FORBIDDEN",
      status: API_STATUS.FORBIDDEN,
    },
    server_error: {
      success: false,
      data: [],
      timestamp: new Date().toISOString(),
      message: "Internal server error",
      error_code: "INTERNAL_SERVER_ERROR",
      status: API_STATUS.ERROR,
    },
  };

  return errorResponses[errorType] as GetMeResponse;
}
